import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { JobsService, resolveRecommendedRate, resolveRateEntry } from '../services';

// Stable empty reference so the returned callback identity doesn't churn while loading.
const EMPTY_RATES = {};

/**
 * Fetches recommended rates for an office and exposes resolvers for the
 * recommended average (getRecommendedRate) and the full rate entry —
 * { min, max, average } — for a profession selection (getRateInfo).
 *
 * @param {string} [officeId] - Office (dentist) id; the query is disabled until provided
 */
export const useRecommendedRates = (officeId) => {
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['recommendedRates', officeId],
    queryFn: () => JobsService.getRecommendedRates(officeId),
    enabled: !!officeId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const rates = data || EMPTY_RATES;

  const getRecommendedRate = useCallback(
    (selection) => resolveRecommendedRate(rates, selection),
    [rates],
  );

  const getRateInfo = useCallback(
    (selection) => resolveRateEntry(rates, selection),
    [rates],
  );

  return {
    rates,
    getRecommendedRate,
    getRateInfo,
    isLoading: isLoading || isFetching,
    isError,
  };
};
